import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Lock, Monitor, Star, Users, Plus, Upload, RefreshCw } from 'lucide-react';
import OsScroll from './ui/OsScroll';
import { isTauri } from '../utils/tauri';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { vaultDisplayState, restoreProgress } from '../utils/vaultRestore';
import { useVaultStore, selectFilteredItems, openVaultGate } from '../store/vaultStore';
import { refreshVaultFromCloud } from '../services/vaultSync';
import { useTeamVaults } from '../hooks/useTeamVaults';
import { teamItemKey } from '../services/teamVaultService';
import { buildDestinations } from '../utils/vaultDestinations';
import { filterTeamItems, parseTeamCategory } from '../utils/teamVaultSections';
import VaultItemModal, { VAULT_TYPE_META } from './VaultItemModal';
import VaultPassphraseBanner from './VaultPassphraseBanner';
import VaultUpdateRequiredBanner from './VaultUpdateRequiredBanner';
import VaultRekeyElsewhereBanner from './VaultRekeyElsewhereBanner';
import VaultRekeyPushBanner from './VaultRekeyPushBanner';
import VaultDeviceRevokedBanner from './VaultDeviceRevokedBanner';
import VaultImportEnvModal from './VaultImportEnvModal';
import VaultImportExportModal from './VaultImportExportModal';
import VaultUpgradeNotice from './VaultUpgradeNotice';
import VaultSiteIcon, { useSiteIconBase, useSiteKeys } from './VaultSiteIcon';
import { useEscapeToDeselect } from '../hooks/useEscapeToDeselect';
import './Vault.css';
import { itemSummary } from './vault/vaultItemFields';
import { VaultItemDetail } from './vault/VaultItemDetail';
import { VaultTeamItemDetail } from './vault/VaultTeamItemDetail';
export { buildFieldRows } from './vault/vaultItemFields';
export { FieldRow } from './vault/VaultFieldRow';

/** The standalone web vault — the same vault core, in a browser (card 3.8). */
const WEB_VAULT_URL = 'https://vault.apiboo.com';

/**
 * Vault main panel (chat-style: replaces the whole tab area). Left: the item
 * list filtered by the sidebar's category + search. Right: the detail pane
 * for the selected item — envelopes come from the store, the decrypted
 * payload is fetched per selection via vault_get_item and lives only in
 * component state so locking/deselecting drops it. That payload is
 * metadata-only where passkeys are concerned: Rust strips their key material
 * before returning it, so nothing here may write `data.passkeys` back.
 *
 * Titlebar actions (ExtensionTitleBarActions HOST_PANEL_HEADERS) dispatch
 * window CustomEvents this component listens for: 'vault-new-item',
 * 'vault-import-env', 'vault-lock'.
 */
export default function VaultView() {
  const { t } = useTranslation();
  // How many items are staged in vault_incoming and not yet drained.
  //
  // On a fresh device the cloud copy arrives as staged rows BEFORE the drain
  // adopts them, so `items` is legitimately empty for a while. Rendering the
  // ordinary "No items yet" over that window tells someone whose passwords are
  // mid-restore that their vault is empty — the most alarming possible reading
  // of a working sync, and the one the owner hit.
  //
  // Polled rather than event-driven because `vault:changed` fires when the
  // drain FINISHES; nothing announces the staged rows arriving, which is
  // exactly the window this covers. 1s is frequent enough to feel live and
  // cheap enough for one indexed COUNT.
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (!isTauri()) return undefined;
    let alive = true;
    let inFlight = false;
    let timer = null;

    // `vault_sync_pending_count` goes through SyncDb.with_conn, which takes a
    // NON-REENTRANT std::sync::Mutex over the single connection — the same lock
    // every vault write needs, and the one vault_sync.rs:300/306 warn deadlocks
    // if re-entered.
    //
    // The first version of this fired on a bare 1s setInterval with no in-flight
    // guard. Whenever a write held that lock, each tick queued ANOTHER waiter
    // behind it; the waiters piled up, the runtime's workers all ended up
    // blocked on the same mutex, and IPC stalled hard enough that the window
    // stopped repainting and the close button did nothing. Saving any vault item
    // reproduced it.
    //
    // So: never more than one call outstanding, and the timer only re-arms while
    // a restore is actually in flight. A vault sitting idle makes exactly one
    // call per vault:changed and then goes quiet.
    const read = () => {
      if (!alive || inFlight) return;
      inFlight = true;
      invoke('vault_sync_pending_count')
        .then((n) => {
          if (!alive) return;
          const next = typeof n === 'number' ? n : 0;
          setPendingCount(next);
          // Re-arm ONLY while something is still draining. Nothing to report on
          // an idle vault, and this is the loop that has to be able to stop.
          if (next > 0) timer = setTimeout(read, 1500);
        })
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    };

    read();
    const sub = subscribeTauriEvent('vault:changed', read);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      sub?.then?.((un) => un?.());
    };
  }, []);

  const restoreTotalRef = useRef(0);
  if (pendingCount > restoreTotalRef.current) restoreTotalRef.current = pendingCount;
  if (pendingCount === 0) restoreTotalRef.current = 0;
  const { total: restoreTotal, done: restoreDone, pct: restorePct } = restoreProgress(
    restoreTotalRef.current,
    pendingCount
  );
  const status = useVaultStore((s) => s.status);
  const loading = useVaultStore((s) => s.loading);
  const error = useVaultStore((s) => s.error);
  const items = useVaultStore((s) => s.items);
  // Derived from `items` (already held here) rather than via a store selector:
  // several suites mock the vault store partially, and reaching for another
  // export makes this component untestable without standing the whole store up.
  // Same reason VaultItemModal takes `folders` as a prop.
  const folders = useMemo(() => {
    const seen = new Map();
    for (const it of items) {
      const f = typeof it?.index?.folder === 'string' ? it.index.folder.trim() : '';
      if (!f) continue;
      const key = f.toLowerCase();
      if (!seen.has(key)) seen.set(key, f);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [items]);
  // Team destinations for the New Item modal. `buildDestinations` returns an
  // EMPTY list when the user has no team they may write to, and the modal
  // renders no destination control at all in that case — the solo experience
  // is unchanged. Wrapped in useMemo for the same reason `folders` is: it
  // builds a fresh array, so subscribing to it directly would loop.
  const { teams, byTeam: teamVaults } = useTeamVaults();
  const destinations = useMemo(
    () => buildDestinations({ teams, byTeam: teamVaults }),
    [teams, teamVaults]
  );
  const search = useVaultStore((s) => s.search);
  const activeCategory = useVaultStore((s) => s.activeCategory);
  const selectedItemId = useVaultStore((s) => s.selectedItemId);
  const setSelectedItemId = useVaultStore((s) => s.setSelectedItemId);
  // Fresh-array selectors can't be passed to the hook directly (zustand v5
  // getSnapshot caching) — derive from the subscribed slices instead.
  const filtered = useMemo(
    () => selectFilteredItems({ items, activeCategory, search }),
    [items, activeCategory, search]
  );

  // ── team scope ────────────────────────────────────────────────────────────
  //
  // A team selection replaces the item list wholesale; the two lists are never
  // merged. Team selection state lives HERE and not in `vaultStore`, so a team
  // id can never end up in `selectedItemId` — which `VaultItemDetail` feeds
  // straight to `vault_get_item`, a lookup in the personal table.
  const teamCategory = useMemo(() => parseTeamCategory(activeCategory), [activeCategory]);
  const teamEntry = teamCategory ? teamVaults[String(teamCategory.teamId)] : null;
  const teamName = teamCategory
    ? teams.find((team) => String(team?.id) === String(teamCategory.teamId))?.name || ''
    : '';
  // Envelope shape only — `GET /items` never returns `data`, and the body is
  // fetched at the moment of use by the detail pane.
  const teamRows = useMemo(
    () =>
      filterTeamItems(teamEntry?.items, activeCategory, search).map((it) => ({
        id: String(it.id),
        type: it.item_type,
        name: it.name,
        index: it.index || {},
        version: it.version,
        folderId: it.folder_id ?? null,
        permission: it.permission,
      })),
    [teamEntry, activeCategory, search]
  );
  // Website icons (vault plan Part 2): the shared core turns each host into
  // its registrable domain; with the setting off there is no base, so no request.
  const siteIconBase = useSiteIconBase();
  const siteKeys = useSiteKeys(
    [...filtered, ...teamRows].filter((it) => it.type === 'password').map((it) => it.index?.urlHost),
    !!siteIconBase
  );
  const siteKeyOf = (it) => (it.type === 'password' ? siteKeys.get(it.index?.urlHost) || null : null);
  const [selectedTeamItemId, setSelectedTeamItemId] = useState(null);
  // Changing scope drops the team selection: a detail pane left over from the
  // previous team would keep fetching a credential the new view is not showing.
  useEffect(() => {
    setSelectedTeamItemId(null);
  }, [activeCategory]);
  const selectedTeamRow = selectedTeamItemId
    ? teamRows.find((row) => row.id === selectedTeamItemId) || null
    : null;
  const deselectItem = () => {
    setSelectedItemId(null);
    setSelectedTeamItemId(null);
  };
  useEscapeToDeselect(Boolean(selectedItemId || selectedTeamItemId), deselectItem);

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  // `{ teamId, itemId, version, folderId }` while editing a TEAM item — routes
  // the save to the team API with optimistic concurrency instead of to
  // `vault_put_item`. Null for every personal edit and every create.
  const [editTeamContext, setEditTeamContext] = useState(null);
  // Where a NEW item should open pre-filed, when the sidebar's "+" said so.
  // `{ destination, folder, teamFolderId }` or null for a blank dialog. Not
  // merged into `editItem`: an edit and a seeded create are different things,
  // and the modal must not treat a seed as an existing item.
  const [newItemSeed, setNewItemSeed] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  // Import/export from other managers ('import' | 'export' | null).
  const [portMode, setPortMode] = useState(null);
  // Titlebar "Refresh from cloud" in flight. The ref is the re-entry guard the
  // handler reads synchronously (state updates are async, so two clicks in the
  // same tick would both see `refreshing === false`); the state drives the
  // banner.
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);

  // This component only mounts while activeTab === 'vault.panel', so
  // mount-time ensureUnlocked also covers every re-entry into the panel — but
  // it must respect an explicit user "Lock vault": while userLocked is set we
  // skip the auto-unlock and show the locked screen (with the manual Unlock
  // button) instead of silently reloading the key from the OS keychain.
  useEffect(() => {
    if (!isTauri()) return;
    if (useVaultStore.getState().userLocked) return;
    useVaultStore.getState().ensureUnlocked();
  }, []);

  // Rust emits vault:changed after every successful mutation — covers writes
  // from other surfaces (sync, the MCP server) beyond our own actions.
  useEffect(() => {
    if (!isTauri()) return undefined;
    const sub = subscribeTauriEvent('vault:changed', () => {
      useVaultStore.getState().refresh();
    });
    return () => sub.unlisten();
  }, []);

  // Titlebar header actions (ExtensionTitleBarActions dispatches these).
  useEffect(() => {
    /**
     * `vault-new-item` with an optional `detail`.
     *
     * The titlebar "+" dispatches NONE — a blank New Item dialog, exactly as
     * before. The sidebar's per-row "+" and context menus dispatch a seed
     * (`{ destination, folder, teamFolderId }`) so the modal opens already
     * filed where the user clicked. That is the whole difference between the
     * two, so the seed is read here and nowhere else.
     */
    const onNew = (e) => {
      const detail = e?.detail;
      setNewItemSeed(detail && typeof detail === 'object' ? detail : null);
      setEditItem(null);
      // A create must never inherit the team routing of the last EDIT, or the
      // save goes to the team API. `onClose` already clears it; this is the
      // guard for the path that does not go through a close.
      setEditTeamContext(null);
      setItemModalOpen(true);
    };
    const onImport = () => setImportOpen(true);
    const onPortImport = () => setPortMode('import');
    const onPortExport = () => setPortMode('export');
    const onLock = () => useVaultStore.getState().lockVault();
    // Guarded against re-entry: the round trip is several awaits long and the
    // titlebar button stays clickable throughout, so an impatient double-click
    // would otherwise start a second reconcile+pull over the first.
    const onRefresh = () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      setRefreshing(true);
      refreshVaultFromCloud().finally(() => {
        refreshingRef.current = false;
        setRefreshing(false);
      });
    };
    window.addEventListener('vault-new-item', onNew);
    window.addEventListener('vault-import-env', onImport);
    window.addEventListener('vault-import', onPortImport);
    window.addEventListener('vault-export', onPortExport);
    window.addEventListener('vault-refresh', onRefresh);
    window.addEventListener('vault-lock', onLock);
    return () => {
      window.removeEventListener('vault-new-item', onNew);
      window.removeEventListener('vault-import-env', onImport);
      window.removeEventListener('vault-import', onPortImport);
      window.removeEventListener('vault-export', onPortExport);
      window.removeEventListener('vault-refresh', onRefresh);
      window.removeEventListener('vault-lock', onLock);
    };
  }, []);

  const openEdit = (fullItem) => {
    setEditItem(fullItem);
    // Personal edit: clear any team routing left from a previous open, or the
    // save would go to the team API with a personal item's body.
    setEditTeamContext(null);
    // …and any create-time seed, for the same reason in the other direction.
    setNewItemSeed(null);
    setItemModalOpen(true);
  };

  /**
   * Edit a TEAM item. `full` is the body the detail pane already fetched from
   * the audited endpoint, so this opens no second read.
   *
   * The id handed to the modal is the `team:<teamId>:<itemId>` key, never the
   * bare server integer — a bare team id in a field a personal command might
   * one day read is the mis-route this key shape exists to make impossible.
   */
  const openTeamEdit = (row, full) => {
    setEditItem({
      id: teamItemKey(teamCategory.teamId, row.id),
      type: full.item_type || row.type,
      name: full.name ?? row.name,
      data: full.data,
    });
    setEditTeamContext({
      teamId: teamCategory.teamId,
      itemId: row.id,
      version: full.version,
      folderId: full.folder_id ?? null,
    });
    setNewItemSeed(null);
    setItemModalOpen(true);
  };

  const openCreate = () => {
    setEditItem(null);
    setEditTeamContext(null);
    setNewItemSeed(null);
    setItemModalOpen(true);
  };

  if (!isTauri()) {
    // The web app (web.apiboo.com): the vault core runs in a browser since the
    // standalone vault (vault.apiboo.com, card 3.8) — same account, same vault.
    // Folding that page into this panel is the next step (owner 12.10.2026);
    // until then the entry opens the web vault instead of claiming desktop-only.
    return (
      <div className="vault-view">
        <div className="vault-empty">
          <div className="vault-empty-icon">
            <Monitor size={24} />
          </div>
          <span className="vault-empty-title">{t('vault.webTitle')}</span>
          <span className="vault-empty-sub">{t('vault.webText')}</span>
          <a className="vault-empty-link" href={WEB_VAULT_URL} target="_blank" rel="noopener noreferrer">
            {t('vault.webOpen')}
          </a>
        </div>
      </div>
    );
  }

  if (!status || !status.unlocked) {
    // A Protected vault gets the gate, not this screen's "click Unlock" —
    // there is a passphrase to type and `ensureUnlocked` cannot supply it
    // (CONTRACT §6.1). A KeyringOnly vault keeps today's behaviour exactly.
    const needsSecret = !!status?.protected;
    return (
      <div className="vault-view" style={{ flexDirection: 'column' }}>
        {/* A vault from a newer Apiboo is always locked: the update line
            belongs here too, not only above the item list (card 1.1). */}
        <VaultUpdateRequiredBanner />
        {/* No gate host here — App.js root-mounts the ONLY one. This used to
            hold an interim mount "until the root mount lands", and keeping it
            after that was not harmless: child effects run before parent
            effects, so on any startup with this panel mounted THIS instance
            claimed the gate. The modal then rendered inside this panel's
            subtree — invisible from every other tab (a DB Manager "unlock to
            save" prompt simply never appeared) and stackable when workspace
            keep-alive mounted the panel twice. */}
        <div className="vault-empty">
          <div className="vault-empty-icon">
            <Lock size={24} />
          </div>
          <span className="vault-empty-title">
            {needsSecret ? t('vault.unlockTitle', 'Vault locked') : t('vault.lockedTitle')}
          </span>
          <span className="vault-empty-sub">
            {needsSecret
              ? t('vault.unlockSubtitle', 'Enter your passphrase to open it.')
              : t('vault.lockedSubtitle')}
          </span>
          {error && <span className="vault-modal-error">{error}</span>}
          <button
            type="button"
            disabled={loading}
            onClick={() =>
              needsSecret ? openVaultGate('vault') : useVaultStore.getState().ensureUnlocked()
            }
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
            style={{ marginTop: 8 }}
          >
            {loading ? t('vault.unlocking') : t('vault.unlock')}
          </button>
        </div>
      </div>
    );
  }

  const selected = selectedItemId ? items.find((it) => it.id === selectedItemId) : null;
  const sorted = [...filtered].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
  );

  return (
    // Column shell so the passphrase banner sits ABOVE the list/detail split
    // (CONTRACT §6.5). Inline styles rather than two new classnames, matching
    // the layout tweaks already used throughout this file.
    <div className="vault-view" style={{ flexDirection: 'column' }}>
      {/* No gate host here either — see the locked branch above. */}
      <VaultUpdateRequiredBanner />
      <VaultRekeyElsewhereBanner />
      <VaultRekeyPushBanner />
      <VaultDeviceRevokedBanner />
      <VaultPassphraseBanner />
      <VaultUpgradeNotice onExport={() => setPortMode('export')} />
      {/* The round trip is usually sub-second, but when it isn't (a cold
          engine task, a slow link, a device with a real backlog to push) a
          titlebar button that stays silent reads as broken. This is also the
          only signal when the refresh finds nothing new — the list is
          identical, so without it a working refresh and a dead button look
          exactly the same. */}
      {refreshing && (
        <div className="vault-pass-banner vault-sync-banner">
          <span className="vault-pass-banner-icon">
            <RefreshCw size={13} className="vault-sync-spin" />
          </span>
          <div className="vault-pass-banner-texts">
            <span className="vault-pass-banner-text">{t('vault.refreshing')}</span>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minWidth: 0, minHeight: 0 }}>
      {teamCategory ? (
        /* A TEAM scope. Checked before the personal empty/restoring states on
           purpose: those describe the PERSONAL vault, and a user whose own
           vault is empty but who holds a team grant must still see the team's
           items rather than "your vault is empty, create your first item". */
        <>
          <div className="vault-list-pane">
            <OsScroll className="vault-list" style={{ flex: 1, minHeight: 0 }}>
              <div className="vault-list-inner">
                {teamRows.length === 0 ? (
                  <div className="vault-empty" style={{ padding: '24px 12px' }}>
                    <span className="vault-empty-sub">
                      {search.trim() ? t('vault.noMatches') : t('vault.emptyCategory')}
                    </span>
                  </div>
                ) : (
                  [...teamRows]
                    .sort((a, b) =>
                      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
                        sensitivity: 'base',
                      })
                    )
                    .map((row) => {
                      const rowMeta = VAULT_TYPE_META[row.type] || VAULT_TYPE_META.custom;
                      const RowIcon = rowMeta.Icon;
                      const sub = itemSummary(row, t) || t(rowMeta.labelKey);
                      return (
                        <div
                          key={row.id}
                          className={`vault-item-row${
                            row.id === selectedTeamItemId ? ' vault-item-row--active' : ''
                          }`}
                          onClick={() => setSelectedTeamItemId(row.id === selectedTeamItemId ? null : row.id)}
                        >
                          <div className="vault-item-icon">
                            <VaultSiteIcon base={siteIconBase} siteKey={siteKeyOf(row)} Fallback={RowIcon} />
                          </div>
                          <div className="vault-item-texts">
                            <span className="vault-item-name">{row.name}</span>
                            <span className="vault-item-sub">{sub}</span>
                          </div>
                          {/* Read-only is worth saying on the row: it decides
                              whether the detail pane offers Edit at all, and a
                              user should know before they open it. */}
                          {row.permission !== 'write' && (
                            <span className="vault-item-badge" title={t('vault.teamReadOnly', 'Read-only')}>
                              <Users size={11} aria-hidden />
                            </span>
                          )}
                        </div>
                      );
                    })
                )}
              </div>
            </OsScroll>
          </div>

          {selectedTeamRow ? (
            <VaultTeamItemDetail
              key={`${teamCategory.teamId}:${selectedTeamRow.id}`}
              teamId={teamCategory.teamId}
              teamName={teamName}
              envelope={selectedTeamRow}
              onEdit={openTeamEdit}
              onClose={deselectItem}
              siteIconBase={siteIconBase}
              siteKey={siteKeyOf(selectedTeamRow)}
            />
          ) : (
            <div className="vault-detail">
              <div className="vault-empty">
                <span className="vault-empty-sub">{t('vault.selectItem')}</span>
              </div>
            </div>
          )}
        </>
      ) : vaultDisplayState(items.length, pendingCount) === 'restoring' ? (
        /* Restoring, NOT empty. Same slot, opposite meaning — see pendingCount. */
        <div className="vault-empty" role="status" aria-live="polite">
          <div className="vault-empty-icon vault-empty-icon--busy">
            <RefreshCw size={24} />
          </div>
          <span className="vault-empty-title">{t('vault.restoringTitle')}</span>
          <span className="vault-empty-sub">
            {t('vault.restoringSub', { count: pendingCount })}
          </span>
          <div
            className="vault-restore-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={restoreTotal}
            aria-valuenow={restoreDone}
          >
            <div
              className="vault-restore-bar-fill"
              style={{
                width: `${restorePct}%`,
              }}
            />
          </div>
        </div>
      ) : vaultDisplayState(items.length, pendingCount) === 'empty' ? (
        <div className="vault-empty">
          <div className="vault-empty-icon">
            <Lock size={24} />
          </div>
          <span className="vault-empty-title">{t('vault.emptyTitle')}</span>
          <span className="vault-empty-sub">{t('vault.emptySubtitle')}</span>
          {/* Two ways in, not one. An empty vault is exactly when someone is most
              likely to be arriving WITH secrets already (a .env, another manager's
              export) rather than typing their first one by hand — and the import
              affordance is otherwise only a small unlabelled toolbar icon. Same
              `setPortMode('import')` the toolbar fires, so there is one import
              path, not a second. */}
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              onClick={openCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Plus size={14} /> {t('vault.createFirst')}
            </button>
            <button
              type="button"
              onClick={() => setPortMode('import')}
              className="px-4 py-2 rounded-lg transition-colors modern-button text-sm vault-empty-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Upload size={14} /> {t('vault.emptyImport')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="vault-list-pane">
            <OsScroll className="vault-list" style={{ flex: 1, minHeight: 0 }}>
              <div className="vault-list-inner">
                {sorted.length === 0 ? (
                  <div className="vault-empty" style={{ padding: '24px 12px' }}>
                    <span className="vault-empty-sub">
                      {search.trim() ? t('vault.noMatches') : t('vault.emptyCategory')}
                    </span>
                  </div>
                ) : (
                  sorted.map((it) => {
                    const meta = VAULT_TYPE_META[it.type] || VAULT_TYPE_META.custom;
                    const RowIcon = meta.Icon;
                    const sub = itemSummary(it, t) || t(meta.labelKey);
                    return (
                      <div
                        key={it.id}
                        className={`vault-item-row${
                          it.id === selectedItemId ? ' vault-item-row--active' : ''
                        }`}
                        onClick={() => setSelectedItemId(it.id === selectedItemId ? null : it.id)}
                      >
                        <div className="vault-item-icon">
                          <VaultSiteIcon base={siteIconBase} siteKey={siteKeyOf(it)} Fallback={RowIcon} />
                        </div>
                        <div className="vault-item-texts">
                          <span className="vault-item-name">{it.name}</span>
                          <span className="vault-item-sub">{sub}</span>
                        </div>
                        {it.favorite && (
                          <Star className="vault-item-star" size={12} fill="currentColor" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </OsScroll>
          </div>

          {selected ? (
            <VaultItemDetail
              envelope={selected}
              onEdit={openEdit}
              onClose={deselectItem}
              siteIconBase={siteIconBase}
              siteKey={siteKeyOf(selected)}
            />
          ) : (
            <div className="vault-detail">
              <div className="vault-empty">
                <span className="vault-empty-sub">{t('vault.selectItem')}</span>
              </div>
            </div>
          )}
        </>
      )}
      </div>

      <VaultItemModal
        folders={folders}
        destinations={destinations}
        byTeam={teamVaults}
        isOpen={itemModalOpen}
        onClose={() => {
          setItemModalOpen(false);
          setEditTeamContext(null);
          setNewItemSeed(null);
        }}
        item={editItem}
        teamContext={editTeamContext}
        // Primitives, not the seed object: the modal keys its reset effect on
        // these, and a fresh object identity each render would re-run it.
        initialDestination={newItemSeed?.destination || null}
        initialFolder={newItemSeed?.folder || null}
        initialTeamFolderId={newItemSeed?.teamFolderId ?? null}
      />
      <VaultImportEnvModal isOpen={importOpen} onClose={() => setImportOpen(false)} />
      <VaultImportExportModal
        isOpen={portMode != null}
        mode={portMode || 'import'}
        onClose={() => setPortMode(null)}
      />
    </div>
  );
}
